import { PlusCircleOutlined } from "@ant-design/icons";
import { Button, Form, Space, Table, TableProps, message } from "antd";
import { useEffect, useState } from "react";
import { BsPencilSquare, BsTrashFill } from "react-icons/bs";
import { SettingLoadingMethod, Filter, initLoadingMethod } from "src/entity/settings/terminal-market";
import { terminalStore } from "src/stores/terminal-store";
import { componentPalette } from "src/theme/component-palette";
import { useShallow } from "zustand/react/shallow";
import LoadingMethodModal from "./LoadingMethodModal";
import { createLoadingMethod, deleteLoadingMethod, updateLoadingMethod } from "src/services/settings.service";
import { CommonError, CommonSuccess } from "src/entity/common/menus-enum.enum";
import VocsModal from "src/components/Modal";
import axios from "axios";
import { canEditTable } from "../PermissionCheck/PermissionCheck";
import useNavigationMenu from "src/hook/useNavigationMenu";
import { fillZeroByKeyList, formatNumberWithFillZero, parseAndFormatScientificNumber } from "src/utils/process-number";
import { calculateReportVocEmissionMasterChange } from "src/services/terminal-marketing.service";

type OnChange = NonNullable<TableProps<any>["onChange"]>;
type Filters = Parameters<OnChange>[1];

const LoadingMethod = () => {
  const loadingMethod = terminalStore(useShallow((state) => state.loadingMethod));
  const totalLoading = terminalStore(useShallow((state) => state.totalLoading));
  const getAllLoadingMethod = terminalStore(useShallow((state) => state.getLoadingMethod));
  const { permissions } = useNavigationMenu();

  const filterLoadingMethod = terminalStore(useShallow((state) => state.filterLoadingMethod));
  const getFilterLoadingMethod = terminalStore(useShallow((state) => state.getFilterLoadingMethod));

  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [mode, setMode] = useState<"insert" | "update">("insert");
  const [showModal, setShowModal] = useState(false);
  const [loadingMethodProps, setLoadingMethodProps] = useState<SettingLoadingMethod>(initLoadingMethod);
  const [form] = Form.useForm();
  const [loadingId, setLoadingId] = useState<string>("");
  const [filter, setFilter] = useState<Filters>({});
  const [messageApi, contextHolder] = message.useMessage();
  const [isDuplicate, setIsDuplicate] = useState<boolean>(false);

  const onCloseModal = () => {
    setShowModal(false);
    setIsDuplicate(false);
    const formValue = form.getFieldsValue();
    if (formValue) {
      form.setFields(
        Object.keys(formValue).map((field) => ({
          name: field,
          errors: [],
        })),
      );
    }
  };

  const onOpenModal = (mode: "insert" | "update", data: SettingLoadingMethod) => {
    setLoadingMethodProps(data);
    setMode(mode);
    setShowModal(true);
    form.setFieldsValue({
      ...fillZeroByKeyList(data, {
        sFactor: 2,
      }),
    });
    setLoadingId(data._id);
  };

  const calPagination = (current?: number) => {
    if (current) {
      return (current - 1) * 10;
    }
  };

  const createParamFilter = () => {
    let queryParam = "";
    let offset = 0;
    let limit = pageSize;

    if (current) {
      let resultCalOffset = calPagination(current);
      if (resultCalOffset) {
        offset = resultCalOffset;
      }
      queryParam += `&offset=${offset}`;
    }
    if (limit) {
      queryParam += `&limit=${limit}`;
    }

    if (filter) {
      const str = Object.entries(filter)
        .filter(([, value]) => value)
        .map(([key, value]) => `&${key}=[${value?.join(",")}]`)
        .join("");
      queryParam += str;
    }

    return queryParam;
  };

  const onSave = async (mode: "insert" | "update", data: SettingLoadingMethod) => {
    try {
      let response;
      setLoading(true);
      if (mode == "insert") {
        response = await createLoadingMethod(data);
      } else {
        if (loadingId) {
          data = {
            ...data,
            _id: loadingId,
          };
          response = await updateLoadingMethod(data);
        }
      }
      if (response?.data.code === 200) {
        setLoading(false);
        setShowModal(false);
        messageApi.open({
          type: "success",
          content: CommonSuccess.SAVED_SUCCESSFULL,
        });
        fetchLoadingMethod();
        getFilterLoadingMethod();
      } else {
        setLoading(false);
        messageApi.open({
          type: "error",
          content: CommonError.SOMETHING_WRONG,
        });
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error?.response?.status === 409) {
          setIsDuplicate(true);
        } else {
          setIsDuplicate(false);
        }
      }
      console.error(error);
    } finally {
      setLoading(false);
      processCalculateReport(loadingId);
    }
  };

  const processCalculateReport = async (loadingId: string) => {
    try {
      setLoading(true);
      await calculateReportVocEmissionMasterChange({
        chemicalId: "",
        loadingById: loadingId,
        emissionControlId: "",
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const mapFilter = (key: string): Filter[] | [] => {
    const resultFilter = filterLoadingMethod.find((item) => item.key === key)?.filter;
    if (resultFilter) {
      const filter: Filter[] = resultFilter?.map((value: any) => {
        let text = value;
        return {
          text,
          value: value,
        };
      });
      return filter;
    } else {
      return [];
    }
  };

  const showDeleteConfirm = async (value: SettingLoadingMethod) => {
    const { _id } = value;
    VocsModal.delete({
      onOk: async () => {
        if (_id) {
          handleDelete(_id);
        }
      },
    });
  };

  const handleDelete = async (id: string) => {
    try {
      setLoading(true);
      const response = await deleteLoadingMethod(id);
      if (response.data.code === 200) {
        setLoading(false);
        messageApi.open({
          type: "success",
          content: CommonSuccess.DELETE_SUCCESSFULL,
        });
        fetchLoadingMethod();
      } else {
        setLoading(false);
        messageApi.open({
          type: "error",
          content: CommonError.SOMETHING_WRONG,
        });
      }
    } catch (error) {
      console.error(error);
      messageApi.open({
        type: "error",
        content: CommonError.SOMETHING_WRONG,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchLoadingMethod = async () => {
    const query = createParamFilter();
    setLoading(true);
    await getAllLoadingMethod(query);
    setLoading(false);
  };

  const onTableChange: TableProps<SettingLoadingMethod>["onChange"] = (pagination: any, filters: any) => {
    let current = pagination.current;
    setFilter(filters);
    setCurrent(current);
  };

  useEffect(() => {
    fetchLoadingMethod();
    getFilterLoadingMethod();
  }, []);

  useEffect(() => {
    fetchLoadingMethod();
  }, [current, filter]);

  const columns: TableProps<SettingLoadingMethod>["columns"] = [
    {
      title: "No.",
      align: "center",
      render: (_, __, index) => <div>{index + 1 + (current - 1) * pageSize}</div>,
    },
    {
      title: "Loading By",
      dataIndex: "loadingBy",
      filters: mapFilter("loadingBy"),
    },
    {
      title: "S Factor",
      dataIndex: "sFactor",
      filters: mapFilter("sFactor"),
      render: (value) => <div>{formatNumberWithFillZero(value, 2)}</div>,
    },
    {
      title: "Remark",
      dataIndex: "remark",
    },
    {
      title: "Action",
      key: "action",
      render: (value, record) => (
        <Space>
          <Button
            className="border-transparent bg-transparent"
            size={"small"}
            onClick={() => {
              onOpenModal("update", record);
            }}
            disabled={!canEditTable(permissions)}
          >
            <BsPencilSquare size={16} color={componentPalette.blueGrey} />
          </Button>
          <Button
            className="border-transparent bg-transparent"
            onClick={() => showDeleteConfirm(value)}
            disabled={!canEditTable(permissions)}
          >
            <BsTrashFill size={16} color={componentPalette.blueGrey} />
          </Button>
        </Space>
      ),
      align: "center",
    },
  ];

  return (
    <>
      {contextHolder}
      {loadingMethodProps && (
        <LoadingMethodModal
          form={form}
          loading={loading}
          open={showModal}
          mode={mode}
          isDuplicate={isDuplicate}
          onDuplicateChange={(isDup) => {
            setIsDuplicate(isDup);
          }}
          onCloseModal={onCloseModal}
          onSave={(mode, data) => onSave(mode, data)}
        />
      )}
      {canEditTable(permissions) && (
        <div style={{ display: "flex", justifyContent: "end", paddingBottom: "10px" }}>
          <Button
            type="primary"
            size="middle"
            onClick={() => onOpenModal("insert", initLoadingMethod)}
            style={{
              display: "flex",
              alignItems: "center",
            }}
            disabled={!canEditTable(permissions)}
          >
            <PlusCircleOutlined />
            New
          </Button>
        </div>
      )}

      <Table
        className="w-full"
        columns={columns}
        dataSource={loadingMethod}
        loading={loading}
        onChange={onTableChange}
        scroll={{ x: "max-content" }}
        pagination={{
          showSizeChanger: false,
          size: "small",
          current,
          pageSize,
          total: totalLoading,
        }}
      />
    </>
  );
};

export default LoadingMethod;
